import { REPORT_TRAIT_FIELDS, useReportTraitOptions } from 'Src/helpers/service/admin/reportTraitOptions';
import * as React from 'react';
import { DatePicker as SitDatePicker, DateRange } from 'Src/components/DatePicker/DatePicker';
import { Switch } from 'Src/components/Switch/Switch';

interface ReportSectionProps {
  number: number;
  title: string;
  hint: string;
  required?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}

interface ReportOption {
  value: string;
  label: string;
  hint: string;
  disabled?: boolean;
}

export function ReportDescription({ children }: { children: React.ReactNode }) {
  return <div className="eReportDescription">{children}</div>;
}

export function ReportSection({ number, title, hint, required = false, action, children }: ReportSectionProps) {
  return (
    <section className="eReportSection">
      <div className="eReportSection__heading">
        <span className="eReportSection__icon">{number}</span>
        <div className="eReportSection__copy">
          <div className="eReportSection__title">
            {title}
            {required ? <span className="eReportSection__required">*</span> : null}
          </div>
          <div className="eReportSection__hint">{hint}</div>
        </div>
        {action ? <div className="eReportSection__action">{action}</div> : null}
      </div>
      {children}
    </section>
  );
}

interface ReportToggleProps {
  name: string;
  label: string;
  hint: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

export function ReportToggle({ name, label, hint, value, onChange }: ReportToggleProps) {
  const available = useReportTraitOptions(!!REPORT_TRAIT_FIELDS[name]);
  React.useEffect(() => {
    if (!available(name) && value) onChange(false);
  }, [available(name), value]);
  if (!available(name)) return null;
  return (
    <div className="eReportToggle">
      <div className="eReportToggle__copy">
        <div className="eReportToggle__label">{label}</div>
        <div className="eReportToggle__hint">{hint}</div>
      </div>
      <Switch name={name} value={value} onChange={event => onChange(event.target.checked)} />
    </div>
  );
}

export function ReportToggleList({ children }: { children: React.ReactNode }) {
  return <div className="eReportToggleList">{children}</div>;
}

export function ReportFormatSelector({
  value,
  pdfValue,
  xlsxValue,
  onChange
}: {
  value: string;
  pdfValue: string;
  xlsxValue: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="eReportFormat" role="radiogroup" aria-label="Output format">
      <button
        type="button"
        role="radio"
        aria-checked={value === pdfValue}
        className={value === pdfValue ? 'eReportFormat__option eReportFormat__option--active' : 'eReportFormat__option'}
        onClick={() => onChange(pdfValue)}
      >
        PDF<span>Ready to print</span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={value === xlsxValue}
        className={
          value === xlsxValue ? 'eReportFormat__option eReportFormat__option--active' : 'eReportFormat__option'
        }
        onClick={() => onChange(xlsxValue)}
      >
        Excel<span>Editable spreadsheet</span>
      </button>
    </div>
  );
}

export function ReportRadioGroup({
  name,
  value,
  options,
  onChange
}: {
  name: string;
  value: string;
  options: ReportOption[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="eReportRadioList" role="radiogroup" aria-label={name}>
      {options.map(option => {
        const isActive = value === option.value;

        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            disabled={option.disabled}
            className={isActive ? 'eReportRadio eReportRadio--active' : 'eReportRadio'}
            onClick={() => onChange(option.value)}
          >
            <span className="eReportRadio__label">{option.label}</span>
            <span className="eReportRadio__hint">{option.hint}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ReportActions({
  onCancel,
  disabled = false,
  submitLabel = 'Generate report',
  submitIcon,
  status = 'Ready to generate'
}: {
  onCancel: () => void;
  disabled?: boolean;
  submitLabel?: string;
  submitIcon?: React.ReactNode;
  status?: React.ReactNode;
}) {
  return (
    <div className="eReportActions">
      <span className="eReportActions__status">{status}</span>
      <button type="button" className="eDsButton eDsButton--secondary" onClick={onCancel}>
        Cancel
      </button>
      <button type="submit" className="eDsButton eDsButton--primary" disabled={disabled}>
        {submitIcon}
        {submitLabel}
      </button>
    </div>
  );
}

export function ReportDateRange({
  dateFrom,
  dateTo,
  onChangeFrom,
  onChangeTo
}: {
  dateFrom: Date;
  dateTo: Date;
  onChangeFrom: (date: Date) => void;
  onChangeTo: (date: Date) => void;
}) {
  return (
    <div className="eReportDateRange">
      <SitDatePicker
        mode="range"
        value={{ start: dateFrom, end: dateTo }}
        onChange={value => {
          if (!value || value instanceof Date) return;
          const range = value as DateRange;
          if (range.start) {
            const start = new Date(range.start);
            start.setHours(0, 0, 1, 0);
            onChangeFrom(start);
          }
          if (range.end) {
            const end = new Date(range.end);
            end.setHours(23, 59, 59, 999);
            onChangeTo(end);
          }
        }}
        format="DD-MM-YYYY"
        placeholder="Select reporting period"
        numberOfMonths={2}
        presets={true}
        syncRangeOnSelect={true}
        className="eReportDateRange__picker"
      />
    </div>
  );
}

export function ReportCheckboxGrid({ items, onChange }: { items: any[]; onChange: (index: number) => void }) {
  const indexedItems = (items || []).map((item, index) => ({ item, index }));
  const isAgeGroup =
    indexedItems.length > 0 &&
    indexedItems.every(({ item }) => Number.isFinite(Number(item.age ?? item.value)));

  const renderChip = ({ item, index }: { item: any; index: number }) => (
    <button
      key={item.age ?? item.value ?? item.name ?? index}
      type="button"
      className={item.isChecked ? 'eReportCheckbox eReportCheckbox--active' : 'eReportCheckbox'}
      aria-pressed={Boolean(item.isChecked)}
      onClick={() => onChange(index)}
    >
      <span className="eReportCheckbox__box">✓</span>
      <span>{item.name || item.text || item.label}</span>
    </button>
  );

  if (isAgeGroup) {
    const stages = [
      { key: 'eyfs', label: 'EYFS', hint: 'Reception', min: 0, max: 0 },
      { key: 'ks1', label: 'KS1', hint: 'Y1–Y2', min: 1, max: 2 },
      { key: 'ks2', label: 'KS2', hint: 'Y3–Y6', min: 3, max: 6 },
      { key: 'ks3', label: 'KS3', hint: 'Y7–Y9', min: 7, max: 9 },
      { key: 'ks4', label: 'KS4', hint: 'Y10–Y11', min: 10, max: 11 },
      { key: 'ks5', label: 'KS5', hint: 'Y12–Y13', min: 12, max: 13 },
      { key: 'other', label: 'Other', hint: '', min: Number.NEGATIVE_INFINITY, max: Number.POSITIVE_INFINITY }
    ];
    const groups = stages
      .map(stage => ({
        ...stage,
        items: indexedItems
          .filter(({ item }) => {
            const age = Number(item.age ?? item.value);
            return stage.key === 'other' ? age < 0 || age > 13 : age >= stage.min && age <= stage.max;
          })
          .sort((left, right) => Number(left.item.age ?? left.item.value) - Number(right.item.age ?? right.item.value))
      }))
      .filter(stage => stage.items.length > 0);

    return (
      <div className="eReportAgeGroups">
        {groups.map(group => (
          <div key={group.key} className="eReportAgeGroup">
            <div className="eReportAgeGroup__title">
              <strong>{group.label}</strong>
              {group.hint ? <span>{group.hint}</span> : null}
            </div>
            <div className="eReportCheckboxGrid">{group.items.map(renderChip)}</div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="eReportCheckboxGrid">{indexedItems.map(renderChip)}</div>
  );
}
